import { z } from 'zod';
import { MoneyStringSchema } from './primitives.js';
/**
 * THE `/stats` TILES OF EVERY DOCUMENT LIST (wave 3B, `specs/erp-gap-analysis.md`
 * "Wave 3 — shared list page").
 *
 * Sales Orders had the one register-wide summary (`/api/sales/orders/stats`);
 * the other nine lists folded their figures out of the page on screen or drew
 * none. PAGINATION.md §3 step 10 names that trap — a figure about everything
 * cannot be folded out of twenty-five rows — so each register now carries a
 * route about everything, in the same shape as the orders one:
 *
 *  - COUNTS are integers. MONEY is a 2dp decimal string (Money on the server,
 *    never a JS number), stated in the company's BASE currency: a tile over
 *    the whole register adds documents in several currencies, and the only
 *    honest sum is at each document's frozen rate (GEN-107, GEN-109a).
 *  - `currency` NAMES THAT BASE on every response, so a tile never has to
 *    guess a symbol and the screen never hard-codes one.
 *  - POSTED-ONLY where it matters: a draft owes nothing, a cancelled document
 *    owes nothing, and neither reaches an "outstanding" or "overdue" figure
 *    (R9 / BR-SAL-04-12 and its purchasing mirror).
 *
 * One file rather than nine inline objects so the shapes can be read side by
 * side, and so the routers each gain one import and one route. The two
 * stats that name a REASON enum (credit notes, purchase returns) live beside
 * their enums instead — importing them here would make a cycle — and the
 * shared date window lives in `pagination.ts` with the paging shape.
 */
/** A percentage as a 2dp string — "37.50". */
export const RatePctStringSchema = z.string().regex(/^\d{1,3}(\.\d{1,2})?$/);
/** Every stats body names the base currency its money is in. */
export const StatsCurrencyField = { currency: z.string() };
export const InvoiceStatsSchema = z
    .object({
    /** Σ (grand total − receipts − standing credit notes) of POSTED invoices above the F-9 tolerance. */
    outstanding: MoneyStringSchema,
    /** Posted, unpaid, due date before the company's today (BR-SAL-04-12). */
    overdueCount: z.number().int().min(0),
    overdueValue: MoneyStringSchema,
    draftCount: z.number().int().min(0),
    /** Σ allocations of POSTED, un-reversed receipts dated this calendar month. */
    collectedThisMonth: MoneyStringSchema,
    ...StatsCurrencyField,
})
    .strict();
export const QuotationStatsSchema = z
    .object({
    /** Sent and awaiting the customer's answer. */
    openCount: z.number().int().min(0),
    openValue: MoneyStringSchema,
    /** accepted ÷ (accepted + rejected + expired) over the last 90 days, by quotation date; "0.00" when none. */
    winRatePct: RatePctStringSchema,
    /** Sent quotations whose validity ends within the next 7 days (today included). */
    expiring7dCount: z.number().int().min(0),
    ...StatsCurrencyField,
})
    .strict();
export const DeliveryNoteStatsSchema = z
    .object({
    draftsToPost: z.number().int().min(0),
    /** Posted notes dated in the company's current week (Monday to Sunday). */
    deliveredThisWeek: z.number().int().min(0),
    /** Confirmed sales orders with nothing delivered yet. */
    ordersAwaitingDelivery: z.number().int().min(0),
    /** Sales orders in `partially_delivered`. */
    partiallyDelivered: z.number().int().min(0),
    ...StatsCurrencyField,
})
    .strict();
export const BillStatsSchema = z
    .object({
    /** Σ (grand total − posted, un-reversed payments) of POSTED bills above the tolerance. */
    payableOutstanding: MoneyStringSchema,
    overdueCount: z.number().int().min(0),
    overdueValue: MoneyStringSchema,
    /** Posted, unpaid, due within the next 7 days (today included). */
    dueIn7d: z.number().int().min(0),
    /** Bills (draft or posted) whose computed match is a variance (BR-PUR-03-14). */
    varianceCount: z.number().int().min(0),
    ...StatsCurrencyField,
})
    .strict();
export const PurchaseOrderStatsSchema = z
    .object({
    /** approved, sent, partially received or received (not yet closed). */
    openOrders: z.number().int().min(0),
    openValue: MoneyStringSchema,
    /** Sent with nothing received yet. */
    awaitingReceipt: z.number().int().min(0),
    /** Received (fully or partly) with nothing billed yet. */
    receivedNotBilled: z.number().int().min(0),
    ...StatsCurrencyField,
})
    .strict();
export const GoodsReceiptStatsSchema = z
    .object({
    draftsToPost: z.number().int().min(0),
    receivedThisWeek: z.number().int().min(0),
    /** Sent purchase orders with nothing received yet. */
    ordersAwaitingReceipt: z.number().int().min(0),
    /** Σ (received − accepted) over POSTED receipts, as a 3dp quantity string. */
    rejectedQty: z.string(),
    ...StatsCurrencyField,
})
    .strict();
export const ImportStatsSchema = z
    .object({
    /** shipped, in transit or at customs. */
    inTransit: z.number().int().min(0),
    /** Of those, arriving within the next 14 days (today included). */
    arriving14d: z.number().int().min(0),
    /** Σ landed total in base of the shipments in transit. */
    valueInTransit: MoneyStringSchema,
    /** Landed (customs or received) with no landed-cost charge entered yet. */
    landedNotAllocated: z.number().int().min(0),
    ...StatsCurrencyField,
})
    .strict();
//# sourceMappingURL=document-stats.js.map